/*
  DOM animation helpers for the donation feed.
  Ported from designer-preview/script.js.

  These operate directly on the DOM because they measure real layout
  positions (FLIP) and spawn short-lived particle nodes. Neither is
  something React state should own.
*/

export const DONATION_FEED_SHIFT_TIME = 720;
export const DONATION_ARRIVAL_CLEAR_TIME = 1100;

const CELEBRATION_COLORS = [
  "#ffffff",
  "#ffef00",
  "#ffca52",
  "#39b54a",
  "#a6d8ff",
  "#f15a24",
];

export function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/* =========================================================
   FEED SHIFT (FLIP)
   ========================================================= */

export function getDonationFeedPositionMap(list) {
  const positions = new Map();
  if (!list) return positions;

  list.querySelectorAll("li[data-feed-id]").forEach((card) => {
    const feedId = card.getAttribute("data-feed-id");
    if (!feedId) return;

    const rect = card.getBoundingClientRect();
    positions.set(feedId, {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  });

  return positions;
}

export function animateDonationFeedShift(list, previousPositions) {
  if (!list || !previousPositions || !previousPositions.size) return;
  if (prefersReducedMotion()) return;

  // Force layout so the post-render rects are accurate.
  // eslint-disable-next-line no-unused-expressions
  list.offsetHeight;

  list.querySelectorAll("li[data-feed-id]").forEach((card) => {
    const feedId = card.getAttribute("data-feed-id");
    const previous = previousPositions.get(feedId);
    if (!feedId || !previous) return;

    const current = card.getBoundingClientRect();
    const deltaX = previous.left - current.left;
    const deltaY = previous.top - current.top;

    if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;

    card.classList.add("donation-card--shifting");

    const animation = card.animate(
      [
        { transform: `translate3d(${deltaX}px, ${deltaY}px, 0)`, offset: 0 },
        { transform: "translate3d(0, 0, 0)", offset: 1 },
      ],
      {
        duration: DONATION_FEED_SHIFT_TIME,
        easing: "cubic-bezier(0.2, 0.72, 0.22, 1)",
        fill: "both",
      },
    );

    animation.finished
      .catch(() => Promise.resolve())
      .then(() => card.classList.remove("donation-card--shifting"));
  });
}

/* =========================================================
   CELEBRATION BURST
   ========================================================= */

function createCelebrationBurst(layer, originX, originY, direction, timers) {
  const burst = document.createElement("div");

  burst.className = "donation-celebration-burst";
  burst.style.left = `${originX}px`;
  burst.style.top = `${originY}px`;

  const particleCount = direction === "middle" ? 22 : 18;

  for (let index = 0; index < particleCount; index += 1) {
    const particle = document.createElement("span");

    let baseAngle;
    let angleSpread;

    if (direction === "left") {
      baseAngle = -205;
      angleSpread = 120;
    } else if (direction === "right") {
      baseAngle = -95;
      angleSpread = 120;
    } else {
      baseAngle = -175;
      angleSpread = 170;
    }

    const angle = (baseAngle + Math.random() * angleSpread) * (Math.PI / 180);

    const distance =
      direction === "middle"
        ? 48 + Math.random() * 118
        : 42 + Math.random() * 104;

    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance - (18 + Math.random() * 34);
    const rotation = -220 + Math.random() * 440;
    const size = 4 + Math.random() * 5;
    const duration = 720 + Math.random() * 420;
    const delay = Math.random() * 85;

    particle.className = "donation-celebration-particle";
    if (index % 4 === 0) particle.classList.add("is-line");

    particle.style.setProperty("--x", `${x}px`);
    particle.style.setProperty("--y", `${y}px`);
    particle.style.setProperty("--r", `${rotation}deg`);
    particle.style.setProperty("--particle-size", `${size}px`);
    particle.style.setProperty("--duration", `${duration}ms`);
    particle.style.setProperty("--delay", `${delay}ms`);
    particle.style.setProperty(
      "--particle-color",
      CELEBRATION_COLORS[index % CELEBRATION_COLORS.length],
    );

    burst.appendChild(particle);
  }

  layer.appendChild(burst);

  timers.push(window.setTimeout(() => burst.remove(), 1500));
}

/*
  Fires three staggered bursts across the top edge of the new card.
  Returns a cleanup function that cancels pending timers and removes
  any bursts still on screen — needed because the dashboard can
  unmount mid-celebration.
*/
export function playDonationArrivalCelebration(layer, card) {
  if (!layer || !card) return () => {};
  if (prefersReducedMotion()) return () => {};

  const cardRect = card.getBoundingClientRect();
  const timers = [];

  const bursts = [
    { x: 0.16, y: 0.14, direction: "left", delay: 0 },
    { x: 0.5, y: 0.02, direction: "middle", delay: 110 },
    { x: 0.84, y: 0.14, direction: "right", delay: 220 },
  ];

  bursts.forEach(({ x, y, direction, delay }) => {
    timers.push(
      window.setTimeout(() => {
        createCelebrationBurst(
          layer,
          cardRect.left + cardRect.width * x,
          cardRect.top + cardRect.height * y,
          direction,
          timers,
        );
      }, delay),
    );
  });

  return () => {
    timers.forEach((timer) => window.clearTimeout(timer));
    layer.querySelectorAll(".donation-celebration-burst").forEach((node) => {
      node.remove();
    });
  };
}
